<template>
    <div class="card">
        <DataTable
            :rows="numberOfRows"
            v-model:first="offset"

            :value="answers"
            :lazy="true"
            paginator
            ref="dt"
            :rowHover="true"
            @page="onPage"
            @sort="onSort"
            sortMode="multiple"
            :metaKeySelection="false"
            dataKey="id"
            :loading="loading"
            :totalRecords="totalRecords"
            responsiveLayout="scroll"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty>No assessments available.</template>
            <template #loading>Loading assessments. Please wait.</template>
            <Column field="id" header="ID" />
            <Column field="assessment_date" header="DATE" style="width: 8rem" sortable>
                <template #body="slotProps">
                    <span class="p-column-title">DATE</span>
                    {{ formatDate(slotProps.data.assessment_date) }}
                </template>
            </Column>
            <!-- Field user_id used so that it can be used for ordering Client -->
            <Column field="user_id" header="CLIENT" style="width: 12rem; text-transform: capitalize" sortable>
                <template #body="slotProps">
                    <span class="p-column-title">CLIENT</span>
                    {{ slotProps.data.user.name + ' ' + slotProps.data.user.surname }}
                </template>
            </Column>

            <!-- Field assessment_code used so that it can be used for ordering Assessment name -->
            <Column field="assessment_code" header="ASSESSMENT" sortable>
                <template #body="slotProps">
                    <span class="p-column-title">ASSESSMENT</span>
                    {{ slotProps.data.assessment.name }}
                </template>
            </Column>

            <Column field="assessment_code" header="RESULT" style="width: 5rem">
                <template #body="slotProps">
                    <span class="p-column-title">RESULT</span>

                    <i v-if="slotProps.data.result === 1" class="pi pi-check " style="color: green; font-size: 1.5rem"></i>
                    <i v-if="slotProps.data.result === 0"  class="pi pi-times" style="color: red; font-size: 1.5rem"></i>
                </template>
            </Column>

<!--            <Column field="author_id" header="AUTHOR ID" style="width: 12rem"/>-->

            <Column field="actions" header="ACTIONS" style="width: 15rem">
                <!-- slotProps -->
                <template #body="slotProps">
                    <span class="p-column-title">ACTIONS</span>
                    <Button icon="pi pi-eye" class="p-button-rounded p-button-info" v-tooltip="'View Answers'" @click="previewDetails(slotProps.data)" />
                    <Button icon="pi pi-briefcase" class="p-button-rounded p-button-warning ml-2" v-tooltip="'Preview Document'" @click="previewDetails(slotProps.data, 'documentPreview')" />
                    <Button
                        v-if="(checkUserPermission(UserPermissions.DELETE_ASSESSMENT_FORM))"
                        icon="pi pi-trash"
                        class="p-button-rounded p-button-danger ml-2"
                        @click="confirmRequest(slotProps.data, onDeleteForm)"
                        v-tooltip="'Delete Attribute'"
                    />
                </template>
            </Column>
        </DataTable>
        <!-- Assessment answers preview dialog for raw data answers -->
        <preview-assessment-answer-dialog v-if="previewRawAnswersDialog" :loading="previewDataLoading" :previewData="previewData" @hide-dialog="previewRawAnswersDialog = false" />
    </div>
</template>

<!-- Make sure the parent component that uses this component provides the answerStore  -->
<script setup>
import PreviewAssessmentAnswerDialog from './PreviewAssessmentAnswerDialog.vue';
import { ref, defineProps, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useAnswerStore } from '../store';
//import { useAttributeStore } from '../../attribute/store';
import { formatDate } from '@/helpers/date-formatter';
import useUserComposable from '@/composables/UserComposable';
import { UserPermissions } from '@/enums';
import { useConfirmComposable } from '@/composables/ConfirmComposable';
import GlobalRequest from '@/api/global-request';
import { DateTime } from 'luxon';
import { buildPagination, getPage, getPerPage, setPage, setPerPage } from '@/composables/PaginationComposable';

const props = defineProps({
    userId: {
        type: [String, Number],
    },
});

const { confirmRequest } = useConfirmComposable();

const router = useRouter();
const answerStore = useAnswerStore();
//const attributeStore = useAttributeStore();

const { answers, criteria, totalRecords, loading, sortParams } = storeToRefs(answerStore);

const previewRawAnswersDialog = ref(false);
const previewData = ref([]);
const previewDataLoading = ref(false);

const { checkUserPermission } = useUserComposable();

const { company_id } = useUserComposable();

const pageList = 'assessmentList';

onMounted(async () => {
    buildPagination(pageList, 0, 10, [{field: "id", order: -1}])

    //console.log('checkUserPermission', checkUserPermission(UserPermissions.DELETE_ASSESSMENT_FORM));

    // normally there is no userId for all assessment answers
    // but for UserSpecificAnswersTable, the component will have userId props,
    // in that case we need to load that up in the criteria
    if (props.userId) {
        criteria.value.user_id = props.userId;
    }
    await loadAnswers();
});

// this function will display the raw assessment answers on a dialog by default
const previewDetails = async (form, mode = 'raw') => {
    if (mode === 'raw') {
        previewRawAnswersDialog.value = true;
        previewDataLoading.value = true;
        await formatPreviewData(form);
        previewDataLoading.value = false;
    } else if (mode === 'documentPreview') {
        return router.push({ name: 'DocumentPreview', params: { assessmentCode: form.assessment.code, formId: form.id} });
    }
};


const formatEntity = (answer, answerType) => {

    if (answerType === '5') {
        const intPart = Math.trunc(Number(answer)); // returns 3
        const floatPart = Number(Number(answer) - intPart); //.toFixed(2)); // return 0.2

        if (floatPart !== 0) {
            return Number(answer).toFixed(2);
        }
    } else if (answerType === '7') {
        return DateTime.fromISO(answer).toLocaleString()
    } else if (answerType === '12') {

        //console.log('answer', answer);

        const input = Number(answer) / 60

        let minutes = Math.trunc(input); // returns 3
        let seconds = Math.round((input - minutes) * 60)

        minutes = minutes.toString().padStart(2, '0')
        seconds = seconds.toString().padStart(2, '0')

        return `00:${minutes}:${seconds}`
    }

    return answer;
}

const formatPreviewData = async (data) => {
    const response = await GlobalRequest.getRequest('v2/assessment/form/' + data.assessment.code + "/" + data.id, {
        params: {
            company_id: company_id.value,
            output: 'json'
        }
    })

    previewData.value = [];

    if (response.statusCode === 200 && response.data && Array.isArray(response.data.steps)) {

        response.data.steps.forEach((step) => {

            if (step.detail && Array.isArray(step.detail)) {
                step.detail.forEach((attribute) => {
                    //console.log('attribute', attribute);

                    //const attributeType = parseInt(attribute.attributeType);
                    const attributeId = attribute.attributeId;
                    const answer = formatEntity(attribute.answer, attribute.attributeType);

                    //console.log('attributeType', attributeType);

                    const tempData = {
                        id: attributeId,
                        label: attribute.label,
                        code: attribute.code,
                        answer: answer,
                    };
                    // now push it to previewData
                    previewData.value.push(tempData);
                })
            }
        })
    }

    /*
    console.log(answers);

    // clean previewData everytime before using
    previewData.value = [];
    // fetch the answers from the data first
    answers = data?.answers?.filter((answer) => answer.user_profile !== 1);
    // now loop through the answers
    // if (answers.length > 0) {
    for (let i = 0; i < answers.length; i++) {
        const answerItem = answers[i];
        // extract exact answer value and the attribute value it holds
        // answerVal is required to be changed later so we shouldnt use const here
        let answerVal = answerItem?.answer;
        // let attributeVal = answerItem?.attribute;
        // get attribute details directly by using id since options and other information is not available here in attribute
        const attributeId = answerItem?.attribute_id;
        const attributeDetails = await attributeStore.fetchAttributeById(attributeId);
        const attributeType = parseInt(attributeDetails?.type);


        console.log('attributeDetails:', attributeDetails)

        // debugger;
        // filter out the free text types P.S - You dont need to preview free text types
        if (![1, 2, 3].includes(parseInt(attributeType))) {
            // there are scenarios where answerVal could be of options index, so we need to check the attribute type below first
            // check if attribute is option type, if yes then extract the option value
            // DROP_DOWN: 8, MULTI_DROP_DOWN: 9, RADIO: 10, CHECKBOX: 11,
            if ([8, 9, 10, 11].includes(parseInt(attributeType))) {
                // extract the option label where answerVal and the option value matches
                const selectedOption = attributeDetails?.options?.find((option) => option.value === parseInt(answerVal));
                // check if the selected option is valid or not
                answerVal = selectedOption?.label ?? 'N/A';
            }
            // Also check if its a numeric type and if it is, then make sure to round up the decimal values to 2 points
            else if (parseInt(attributeType) === 5) {
                // first check if answerVal is empty or not
                // check if its decimal type or normal int
                // for normal int, just display it as it is,
                // and for float type, show decimal upto 2 digits
                answerVal = answerVal ? (answerVal % 1 === 0 ? parseInt(answerVal) : parseFloat(answerVal)?.toFixed(2)) : 'N/A';
            }
            // Also check if its a date type DATE:7
            else if (parseInt(attributeType) === 7) {
                answerVal = answerVal ? formatDate(answerVal) : 'N/A';
            }
            // extract only the required information and assign put it to tempData
            const tempData = {
                id: attributeId,
                label: attributeDetails.label,
                code: attributeDetails.code,
                answer: answerVal,
            };
            // now push it to previewData
            previewData.value.push(tempData);
        }
    }

     */
};

const offset = computed(() => {
    console.log('offset::computed', getPage(pageList), getPerPage(pageList))
    return getPage(pageList) * getPerPage(pageList)
})

const numberOfRows = computed(() => {
    return getPerPage(pageList)
})

const onPage = async (event) => {
    setPage(pageList, event.page)
    setPerPage(pageList, event.rows)

    criteria.value.page = event.page + 1;
    criteria.value.page_size = event.rows;
    await loadAnswers();
};

const onSort = (event) => {
    const sortings = event.multiSortMeta;
    sortParams.value = sortings
        .map((item) => {
            return item.field + ' ' + (item.order == 1 ? 'asc' : 'desc');
        })
        .join(',');
    loadAnswers();
};

const loadAnswers = async () => {
    await answerStore.getAllAnswers(criteria.value, pageList);
};

async function onDeleteForm(formId) {
    console.log(formId)
    await answerStore.deleteAssessmentForm(formId)

    if (props.userId) {
        criteria.value.user_id = props.userId;
    }

    await loadAnswers()
}

</script>
